import {
  chapterTimeZone,
  eventChapterOf,
  findEventEntry,
  localDateKey,
} from "@repo/vihangam-yog-shared";

import { CHAPTER } from "../chapter";
import type { EventGroup } from "../pages/event-registrations/shared";

/**
 * Where an event sits in time: today, coming up, every week (no date) or
 * finished. One copy for the Events list, Reception's picker, the Emails
 * page and Home, so no two screens disagree about what is on today.
 */

/** The catalogue doesn't say when this event ends: assume 4 hours. */
const ASSUMED_LENGTH_MS = 4 * 60 * 60 * 1000;

/** The part of an event group the timing rules read. */
export type TimedEvent = Pick<
  EventGroup,
  "eventSlug" | "eventStartsAt" | "eventEndsAt"
>;

export interface PickerGroups<T extends TimedEvent> {
  /** On today's date in the event's own time zone, started or not (and
   *  still here after it ends, for late check-ins). Soonest first. */
  today: T[];
  /** Dated, not today and not finished. Soonest first. */
  comingUp: T[];
  /** No date: the weekly events. In the order given. */
  everyWeek: T[];
  /** Ended on an earlier day. Most recent first. */
  finished: T[];
}

/** Milliseconds for an ISO string; null when missing or unreadable. */
function instant(iso: string | null): number | null {
  if (!iso) {
    return null;
  }
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function byStart(a: TimedEvent, b: TimedEvent): number {
  return (instant(a.eventStartsAt) ?? 0) - (instant(b.eventStartsAt) ?? 0);
}

/** The zone an event's date is judged in: its own chapter's (Christchurch
 *  runs on New Zealand time, even in the Australian build), else this
 *  build's when the catalogue doesn't know the event. */
function eventTimeZone(slug: string): string {
  const entry = findEventEntry(slug);
  return entry ? chapterTimeZone(eventChapterOf(entry)) : CHAPTER.timezone;
}

/**
 * Over once its end time has passed, or 4 hours after the start when the
 * catalogue has no end. An event in progress has not finished, and an
 * undated weekly event never does.
 */
export function eventHasFinished(
  group: TimedEvent,
  now: Date = new Date(),
): boolean {
  const start = instant(group.eventStartsAt);
  const finishedAt =
    instant(group.eventEndsAt) ??
    (start === null ? null : start + ASSUMED_LENGTH_MS);
  return finishedAt !== null && finishedAt < now.getTime();
}

/**
 * On today's date in the event's own time zone, whether it has started
 * or not. An event whose end falls on a later date counts on every day it
 * spans.
 */
export function isEventToday(
  group: TimedEvent,
  now: Date = new Date(),
): boolean {
  const startsAt = group.eventStartsAt;
  if (!startsAt || instant(startsAt) === null) {
    return false;
  }
  const zone = eventTimeZone(group.eventSlug);
  const todayKey = localDateKey(now.toISOString(), zone);
  const startKey = localDateKey(startsAt, zone);
  const endsAt = group.eventEndsAt;
  const endKey =
    endsAt && instant(endsAt) !== null ? localDateKey(endsAt, zone) : startKey;
  return startKey <= todayKey && todayKey <= endKey;
}

/** Events split the way every picker shows them: Today, Coming up,
 *  Every week and Finished. */
export function groupEventsForPicker<T extends TimedEvent>(
  groups: readonly T[],
  now: Date = new Date(),
): PickerGroups<T> {
  const today: T[] = [];
  const comingUp: T[] = [];
  const everyWeek: T[] = [];
  const finished: T[] = [];
  for (const group of groups) {
    if (instant(group.eventStartsAt) === null) {
      everyWeek.push(group);
    } else if (isEventToday(group, now)) {
      today.push(group);
    } else if (eventHasFinished(group, now)) {
      finished.push(group);
    } else {
      comingUp.push(group);
    }
  }
  today.sort(byStart);
  comingUp.sort(byStart);
  finished.sort((a, b) => byStart(b, a));
  return { today, comingUp, everyWeek, finished };
}

/**
 * Today's events among the groups the backend returned, soonest first.
 * The data already holds exactly what this build serves (Australia and
 * New Zealand in the Australian build, Singapore in its own), so there is
 * no chapter filter here: filtering the catalogue by this build's chapter
 * would drop Christchurch.
 */
export function eventsTodayFromData<T extends TimedEvent>(
  groups: readonly T[],
  now: Date = new Date(),
): T[] {
  return groups.filter((group) => isEventToday(group, now)).sort(byStart);
}
